import {
  AddManyParams,
  WixPatternsContainer,
  CollectionState,
  FiltersMap,
} from '@wix/bex-core';
import {
  ToolbarCollectionState,
  ToolbarCollectionStateParamsInitParams,
} from './ToolbarCollectionState';
import { makeObservable, observable } from 'mobx';
import { TableVirtualState } from './TableVirtualState';
import { DragAndDropState } from '../components/DragAndDrop';

export interface InfiniteScrollTableStateParams<T, F extends FiltersMap> {
  readonly collection: CollectionState<T, F>;
  readonly container: WixPatternsContainer;
  readonly toolbar?: ToolbarCollectionState<T, F>;
}

interface TableDragAndDropStateBase<T, F extends FiltersMap> {
  getStickyColumnsCount: (params: { stickyColumns: number }) => number;
  init: (params: { a11yContainer?: HTMLElement | null | undefined }) => void;
  dragAndDrop: {
    dnd: DragAndDropState<T, F>;
  };
}

export interface InfiniteScrollTableStateInitParams
  extends ToolbarCollectionStateParamsInitParams {
  hasMultiLevelSorting?: boolean;
}

export class InfiniteScrollTableState<T, F extends FiltersMap> {
  readonly toolbar;
  readonly virtual;

  tableDragAndDropState: TableDragAndDropStateBase<T, F> | null = null;
  tableDragAndDropStateInitialized = false;

  constructor(params: InfiniteScrollTableStateParams<T, F>) {
    this.toolbar =
      params.toolbar ??
      new ToolbarCollectionState({
        collection: params.collection,
        container: params.container,
        componentType: 'Table',
      });

    this.virtual = new TableVirtualState({
      table: this,
    });

    makeObservable(this, {
      tableDragAndDropStateInitialized: observable.ref,
    });
  }

  // backwards compat
  get table() {
    return this.toolbar;
  }

  get collection() {
    return this.table.collection;
  }

  get tableState() {
    return this;
  }

  get itemsContentWidth() {
    return this.toolbar.itemsContentRect.rect.width ?? 0;
  }

  get itemsToRenderCount() {
    return this.collection.result.size;
  }

  init(params: InfiniteScrollTableStateInitParams) {
    return this.table.init(params);
  }

  get keyedItems() {
    return this.collection.result.keyedItems;
  }

  getStickyColumnsCount(params: {
    stickyColumns?: number;
    horizontalScroll?: boolean;
    showSelection?: boolean;
    useNewStickyColumns?: boolean;
  }) {
    const { horizontalScroll } = params;

    if (!horizontalScroll) {
      return params.stickyColumns;
    }

    const { tableDragAndDropState, tableDragAndDropStateInitialized, toolbar } =
      this;

    const {
      customColumnsState,
      customColumnsProps,
      customColumnsStateInitialized,
    } = toolbar;

    const { useNewStickyColumns, showSelection } = params;

    let stickyColumns = params.stickyColumns ?? 0;

    const customColumnsIsEffective =
      customColumnsProps && customColumnsState && customColumnsStateInitialized;

    const reorderDisabledColumnsIsEffective =
      !!customColumnsState?.dragAndDrop?.orderedColumnsByReorderDisabled
        .reorderDisabled?.length;

    if (customColumnsIsEffective) {
      stickyColumns = customColumnsState.getStickyColumnsCount({
        ...params,
        stickyColumns,
      });
    }

    const dragAndDropIsEffective =
      tableDragAndDropState && tableDragAndDropStateInitialized;
    if (dragAndDropIsEffective) {
      stickyColumns = tableDragAndDropState.getStickyColumnsCount({
        ...params,
        stickyColumns,
      });
    }

    const checkboxColumnStickyCompat =
      reorderDisabledColumnsIsEffective ||
      useNewStickyColumns ||
      dragAndDropIsEffective;

    if (showSelection && checkboxColumnStickyCompat) {
      stickyColumns += 1;
    }

    return stickyColumns;
  }

  get showEmptyState() {
    return this.toolbar.multi.showEmptyState;
  }

  get showErrorState() {
    return this.toolbar.multi.showErrorState;
  }

  get errorStatus() {
    return this.toolbar.multi.errorStatus;
  }

  get showLoadingState() {
    const { initTask, toolbar } = this;
    return (
      initTask.status.isIdle ||
      initTask.status.isLoading ||
      toolbar.multi.showLoadingState
    );
  }

  get initTask() {
    return this.toolbar.initTask;
  }

  retryErrorState = () => this.toolbar.retryErrorState();

  onAddItemActionComplete = async <R>(
    items: T[],
    params: AddManyParams<T, R> = {},
  ) => {
    return this.toolbar.onAddItemActionComplete(items, params);
  };
}

export { InfiniteScrollTableState as TableState };
